import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ViewModeItem } from "../models/ViewModeItem";
import { ViewModeItems } from "../models/ViewModeItem";
import { SortItem, AvailableSortItems } from "../models/SortItem";
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { ChapterArticle } from "../models/ChapterArticle";
import { SuggestItem } from "../models/SuggestItem";
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface ChapterBlogsState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedCategory: undefined | LookupItem;
  selectedChapter: undefined | LookupItem;
  selectedYearMonth: undefined | LookupItem;
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: ChapterArticle[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: ChapterBlogsState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[2],
  keywords: "",
  suggestions: [],
  selectedCategory: undefined,
  selectedChapter: undefined,
  selectedYearMonth: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  category?: string;
  chapter?: string;
  yearMonth?: string;
  sortBy?: string;
  sortAsc?: boolean;
  path?: string;
  page: number;
  pageSize: number;
};

export const searchChapterBlogsSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchChapterBlogsSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/chapterblogarticlessearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const searchChapterBlogs = createAsyncThunk<
  FacetResult<ChapterArticle>,
  SearchFilter
>(
  "data/searchChapterBlogs",
  async (payload: SearchFilter): Promise<FacetResult<ChapterArticle>> => {
    const response = await fetch("/chapterblogarticlessearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<ChapterArticle>;
  }
);

export const chapterBlogsSlice = createSlice({
  name: "chapterBlogs",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords !== action.payload) {
        state.items = [];
        state.canLoadMore = false;
        state.orderBy = AvailableSortItems[3];
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeCategory: (state, action: PayloadAction<undefined | LookupItem>) => {
      state.items = [];
      state.selectedCategory = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeChapter: (
      state,
      action: PayloadAction<undefined | LookupItem>
    ) => {
      state.items = [];
      state.selectedChapter = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeYearMonth: (
      state,
      action: PayloadAction<undefined | LookupItem>
    ) => {
      state.items = [];
      state.selectedYearMonth = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchChapterBlogs.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchChapterBlogs.fulfilled,
        (state, action: PayloadAction<FacetResult<ChapterArticle>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchChapterBlogs.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchChapterBlogsSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchChapterBlogsSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchChapterBlogsSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeViewMode,
  changeKeywords,
  changeCategory,
  changeChapter,
  changeYearMonth,
  changeInit,
  clearSuggestions
} = chapterBlogsSlice.actions;

export default chapterBlogsSlice.reducer;
